import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import queryString from "query-string";
import { toast } from "react-toastify";

import api from "libs/api";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const TasksContext = createContext();

const TasksProvider = ({ children }) => {
  const { search } = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    page: _page,
    onboarding_created: _onboarding_created,
    onboarding: _onboarding,
    ...filters
  } = queryString.parse(search);
  const default_sort = "updated_at:desc";

  const [init_loading, setInitLoading] = useState(true);
  const [init_data_length, setInitDataLength] = useState(0);

  const {
    isLoading: loading,
    data: tasks,
    isRefetching,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ["tasks", filters],
    queryFn: () =>
      api.get(
        `/client/tasks?${queryString.stringify({
          ...filters,
          status:
            task_status_data
              ?.filter(
                (item) =>
                  filters?.status?.split(",").includes(item?.key) &&
                  item?.key !== "canceled",
              )
              .map((item) => item?.value)
              .join(",") || undefined,
          priority:
            task_priority_data
              ?.filter((item) =>
                filters?.priority?.split(",").includes(item?.key),
              )
              .map((item) => item?.value)
              .join(",") || undefined,
          sort: filters?.sort || default_sort,
        })}`,
      ),
  });

  useEffect(() => {
    if (isError) {
      navigate("/tasks", { replace: true });
      toast.error(t("Something went wrong."));
    }
  }, [navigate, t, isError]);

  useEffect(() => {
    if (isSuccess && tasks) {
      setInitDataLength((prev) => (!!init_loading ? tasks?.length : prev));
      setInitLoading(false);
    }
  }, [isSuccess, tasks, init_loading]);

  const getLatestTask = async () => {
    try {
      const tasks = await api.get(`/client/tasks?sort=updated_at%3Adesc`);

      return tasks && tasks.length > 0 ? tasks[0] : null;
    } catch (err) {
      console.log(err);
      return undefined;
    }
  };

  const filterClearAll = () => {
    const query = filters?.sort
      ? `?${queryString.stringify({ sort: filters.sort })}`
      : "";

    navigate(`/tasks${query}`);
  };

  const applyFilters = (newFilters) => {
    const merged = { ...filters };

    Object.entries(newFilters).forEach(([name, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === false ||
        value === ""
      ) {
        delete merged[name];
      } else {
        merged[name] = typeof value === "boolean" ? value : value?.key;
      }
    });

    navigate(`/tasks?${queryString.stringify(merged)}`);
  };

  const filterHandler = (name, value, accumulate = false) => {
    let accumulated_value = value;

    if (accumulate) {
      const currentValues = filters[name] ? filters[name].split(",") : [];

      if (value) {
        if (currentValues.includes(value)) {
          const filteredValues = currentValues.filter((val) => val !== value);
          accumulated_value =
            filteredValues.length > 0 ? filteredValues.join(",") : undefined;
        } else {
          currentValues.push(value);
          accumulated_value = currentValues.join(",");
        }
      } else {
        accumulated_value =
          currentValues.length > 0 ? currentValues.join(",") : undefined;
      }
    }

    navigate(
      `/tasks?${queryString.stringify({
        ...filters,
        [name]: accumulated_value || undefined,
      })}`,
    );
  };

  const updateTask = (id, values) =>
    new Promise(async (resolve, reject) => {
      const taskQueries = queryClient.getQueriesData({
        queryKey: ["tasks"],
        predicate: (query) => query.queryKey[0] === "tasks",
      });

      const previousQueryStates = new Map();

      try {
        taskQueries.forEach(([queryKey, queryData]) => {
          if (!queryData) return;

          previousQueryStates.set(queryKey, [...queryData]);

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData) return [];

            const queryFilters = queryKey[1] || {};

            return oldData
              .map((task) => {
                if (task._id === id) {
                  const updatedTask = { ...task, ...values };

                  if (queryFilters.status) {
                    const statusValue = task_status_data?.find(
                      (item) => item?.key === queryFilters.status,
                    )?.value;

                    if (statusValue && updatedTask.status !== statusValue) {
                      return null;
                    }
                  }

                  if (queryFilters.type) {
                    const typeValue = task_status_data?.find(
                      (item) => item?.key === queryFilters.type,
                    )?.value;

                    if (typeValue && updatedTask.priority !== typeValue) {
                      return null;
                    }
                  }

                  return updatedTask;
                }

                return task;
              })
              .filter(Boolean);
          });
        });

        await api.put(`/client/tasks/${id}`, values);

        await queryClient.invalidateQueries({
          queryKey: ["tasks"],
        });

        resolve();
      } catch (err) {
        taskQueries.forEach(([queryKey]) => {
          const originalData = previousQueryStates.get(queryKey);
          if (originalData) {
            queryClient.setQueryData(queryKey, originalData);
          }
        });

        console.error(err);
        toast.error(t("Something went wrong."));
        reject(err);
      }
    });

  const value = useMemo(() => {
    return {
      data: tasks,
      init_data_length,
      loading: loading && !isRefetching,
      init_loading,
      filters,
      filterHandler,
      filterClearAll,
      applyFilters,
      updateTask,
      getLatestTask,
    };
    // eslint-disable-next-line
  }, [
    tasks,
    loading,
    isRefetching,
    init_data_length,
    filters,
    init_loading,
    updateTask,
    getLatestTask,
    applyFilters,
  ]);

  return (
    <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
  );
};

const useTasks = () => useContext(TasksContext);
export { TasksContext, useTasks };
export default TasksProvider;
